// src/utils/resolveAssessmentId.js
//
// Picks WHICH assessment a user is served, from the member-funnel age axis.
// Adults take the org's configured assessment (org.activeAssessmentId, as before);
// minors are routed to an age-appropriate config instead — even when the org's
// default is an adult deep-dive. This is a safety boundary as much as a feature:
// the youth config is linear with NO clinical trigger modules (GAD-7 / PHQ-9 /
// substance / gambling), so a kid/teen never lands in an adult clinical deep-dive.
//
// Mirrors the resolveSurface / ageTier pattern: a pure function with safe
// fallbacks so nothing breaks for existing users (no ageTier -> adult default).
//
// Precedence, per tier:
//   1. Org override  — org.assessmentByTier[tier]   (lets a client customize)
//   2. Code default  — DEFAULT_TIER_ASSESSMENTS[tier] (kid/teen -> youth_v1)
//   3. Adult / unknown -> org.activeAssessmentId || 'core_v1'

// Built-in, content-safe defaults for the minor tiers. Both kid and teen map to
// the single youth config that exists today; when distinct kid vs teen configs
// are authored, slot their ids in here (or set org.assessmentByTier per client).
const DEFAULT_TIER_ASSESSMENTS = {
  kid: 'youth_v1',
  teen: 'youth_v1',
};

const ADULT_FALLBACK = 'core_v1';

// AUDIENCE SEPARATOR. The id above is what the org ASKED for; this is the
// check applied to what actually loaded. Adults never get a Youth-audience
// set (a parent should not be asked about chores and homework) and minors
// only ever get a Youth one (an 'Adult' or 'Universal' set carries clinical
// deep-dives). Enforced at load time in useAssessmentLogic regardless of how
// the org is configured, so a mis-set dropdown cannot cross the line.
export const YOUTH_FALLBACK_ID = 'youth_v1';
export const ADULT_FALLBACK_ID = ADULT_FALLBACK;

export const isMinorTier = (ageTier) => ageTier === 'kid' || ageTier === 'teen';

export function audienceAllows(config, ageTier) {
  if (!config) return false;
  const youthSet = config.audience === 'Youth';
  return isMinorTier(ageTier) ? youthSet : !youthSet;
}

export const fallbackIdFor = (ageTier) => (isMinorTier(ageTier) ? YOUTH_FALLBACK_ID : ADULT_FALLBACK_ID);

export function resolveAssessmentId({ ageTier, org, memberType } = {}) {
  const orgByTier = (org && org.assessmentByTier) || {};
  const byMember = (org && org.assessmentByMemberType) || {};
  const adultDefault = (org && org.activeAssessmentId) || ADULT_FALLBACK;

  // Adults (and anyone missing an ageTier) get the org's configured assessment.
  // A member type can carry its own: a Parent on a youth-sport org answers
  // about their player (parent_v1), not about themselves.
  if (ageTier !== 'kid' && ageTier !== 'teen') {
    return (memberType && byMember[memberType]) || orgByTier.adult || adultDefault;
  }

  // Minors: org override wins, then the safe built-in default.
  return orgByTier[ageTier] || DEFAULT_TIER_ASSESSMENTS[ageTier] || adultDefault;
}
